// Per-coffee view. Three tabs stacked on the same route:
//
//   Overview     hero roast + metadata + quick stats
//   Timeline     every roast of this coffee, newest first
//   Consistency  scorecard from get_consistency_report
//
// Data comes from four independent REST calls fired in parallel. Any one of
// them failing doesn't block the others — the failing tab surfaces its own
// error inline. This matches the operator's mental model: if the consistency
// report has a bad day, I can still browse the timeline.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "../useApi";
import type {
  CoffeeProfile,
  ConsistencyReport,
  MetricStats,
  RecipeTrend,
  ReferenceRoastInfo,
  RoastSummary,
} from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { StarRating } from "../components/StarRating";
import { formatDate, formatNumber, formatTime } from "../format";
import { rowTags } from "../rows";

type TabKey = "overview" | "timeline" | "consistency" | "trend";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "timeline", label: "Timeline" },
  { key: "consistency", label: "Consistency" },
  { key: "trend", label: "Trend" },
];

export function CoffeeDetail() {
  const { coffeeId = "" } = useParams();
  const api = useApi();

  // Tab state lives in the query string so deep links like
  // /coffees/x?tab=consistency round-trip cleanly.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab: TabKey =
    rawTab === "timeline" || rawTab === "consistency" || rawTab === "trend"
      ? rawTab
      : "overview";
  const setActiveTab = (key: TabKey) => {
    const sp = new URLSearchParams(searchParams);
    if (key === "overview") sp.delete("tab");
    else sp.set("tab", key);
    setSearchParams(sp, { replace: true });
  };

  // One independent piece of state per endpoint. We keep `err` per source so
  // a failing consistency call doesn't blank out the roast list.
  const [profile, setProfile] = useState<CoffeeProfile | null>(null);
  const [profileErr, setProfileErr] = useState<unknown>(null);

  const [history, setHistory] = useState<RoastSummary[]>([]);
  const [historySummary, setHistorySummary] = useState<string>("");
  const [historyErr, setHistoryErr] = useState<unknown>(null);

  const [reference, setReference] = useState<ReferenceRoastInfo | null>(null);
  const [referenceText, setReferenceText] = useState<string>("");
  const [referenceErr, setReferenceErr] = useState<unknown>(null);

  const [consistency, setConsistency] = useState<ConsistencyReport | null>(null);
  const [consistencyText, setConsistencyText] = useState<string>("");
  const [consistencyErr, setConsistencyErr] = useState<unknown>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setProfile(null);
    setHistory([]);
    setReference(null);
    setConsistency(null);
    setProfileErr(null);
    setHistoryErr(null);
    setReferenceErr(null);
    setConsistencyErr(null);

    const profileP = api
      .getCoffeeProfile(coffeeId)
      .then((r) => setProfile(r.profile))
      .catch(setProfileErr);
    const historyP = api
      .getCoffeeHistory(coffeeId)
      .then((r) => {
        setHistory(r.roasts ?? []);
        setHistorySummary(r.summary ?? "");
      })
      .catch(setHistoryErr);
    const referenceP = api
      .getReferenceRoast(coffeeId)
      .then((env) => {
        setReference(env.data.reference);
        setReferenceText(env.text ?? "");
      })
      .catch(setReferenceErr);
    const consistencyP = api
      .getConsistencyReport(coffeeId)
      .then((env) => {
        setConsistency(env.data.report);
        setConsistencyText(env.text ?? "");
      })
      .catch(setConsistencyErr);

    Promise.allSettled([profileP, historyP, referenceP, consistencyP]).finally(
      () => setLoading(false),
    );
  }, [api, coffeeId]);

  // If the coffee id is unknown *and* we have no history, it's a genuine 404.
  // A coffee with history but no profile record is a real thing — it can
  // happen when roasts were imported before the profile was saved — so we
  // keep rendering.
  if (profileErr && profile == null && !loading && history.length === 0) {
    return (
      <div className="col">
        <div>
          <Link to="/coffees">← Coffees</Link>
        </div>
        <ErrorBox error={profileErr} />
      </div>
    );
  }

  const headerName =
    profile?.name ??
    history[0]?.coffee_label ??
    history[0]?.coffee_id ??
    coffeeId;

  return (
    <div className="col">
      <div>
        <Link to="/coffees">← Coffees</Link>
      </div>

      <div className="card">
        <div className="row">
          <div>
            <h1 style={{ margin: "0 0 4px 0" }}>{headerName}</h1>
            <div className="muted small mono">{coffeeId}</div>
            {profile && (
              <div className="muted small">{buildProfileLine(profile)}</div>
            )}
          </div>
        </div>
        {profile?.tags && profile.tags.length > 0 && (
          <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {profile.tags.map((t) => (
              <span className="badge" key={t}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={`tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <OverviewTab
          profile={profile}
          reference={reference}
          referenceText={referenceText}
          referenceErr={referenceErr}
          history={history}
          loading={loading}
        />
      )}
      {activeTab === "timeline" && (
        <TimelineTab
          history={history}
          summary={historySummary}
          err={historyErr}
          loading={loading}
        />
      )}
      {activeTab === "consistency" && (
        <ConsistencyTab
          report={consistency}
          text={consistencyText}
          err={consistencyErr}
          loading={loading}
        />
      )}
      {activeTab === "trend" && <TrendTab coffeeId={coffeeId} />}
    </div>
  );
}

// --- Trend tab ----------------------------------------------------------
//
// Answers "is my recipe for this coffee + roast level + size getting
// better?" by calling get_recipe_trend with operator-set filters.
// Roast level defaults to "any" so the first render is meaningful even
// before the operator narrows down. Batch size band defaults to (any,
// any) for the same reason.

function TrendTab({ coffeeId }: { coffeeId: string }) {
  const api = useApi();
  const [roastLevel, setRoastLevel] = useState<string>("");
  const [sizeMin, setSizeMin] = useState<string>("");
  const [sizeMax, setSizeMax] = useState<string>("");
  const [trend, setTrend] = useState<RecipeTrend | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const t = await api.getRecipeTrend({
        coffee_id: coffeeId,
        roast_level: roastLevel || undefined,
        batch_size_min_g: sizeMin ? Number(sizeMin) : undefined,
        batch_size_max_g: sizeMax ? Number(sizeMax) : undefined,
      });
      setTrend(t);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Initial fetch only — subsequent fetches are explicit (Apply button).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coffeeId]);

  const verdictClass = (v: string | undefined) => {
    switch (v) {
      case "improving": return "ok";
      case "steady": return "";
      case "declining": return "err";
      default: return "warn"; // too_few
    }
  };

  return (
    <div className="col">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Is my recipe improving?</h2>
        <p className="muted small">
          Filter your rated history of this coffee by roast level (manual
          tag wins, otherwise inferred from development time ratio) and
          batch size, then we compare the most recent 3 rated batches
          to the prior batches.
        </p>
        <div className="filter-bar">
          <label>
            Roast level
            <select
              value={roastLevel}
              onChange={(e) => setRoastLevel(e.target.value)}
            >
              <option value="">any</option>
              <option value="light">light</option>
              <option value="medium">medium</option>
              <option value="full_city">full city</option>
              <option value="dark">dark</option>
            </select>
          </label>
          <label>
            Min batch (g)
            <input
              type="number"
              value={sizeMin}
              onChange={(e) => setSizeMin(e.target.value)}
              placeholder="e.g. 350"
            />
          </label>
          <label>
            Max batch (g)
            <input
              type="number"
              value={sizeMax}
              onChange={(e) => setSizeMax(e.target.value)}
              placeholder="e.g. 550"
            />
          </label>
          <div style={{ alignSelf: "end" }}>
            <button onClick={refresh} disabled={loading}>
              {loading ? "Loading…" : "Apply"}
            </button>
          </div>
        </div>
        <ErrorBox error={err} />
        {trend && (
          <div style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: "baseline", gap: 12 }}>
              <span className={`badge ${verdictClass(trend.verdict)}`}>
                {trend.verdict.replace("_", " ")}
              </span>
              <span className="small">{trend.message}</span>
            </div>
            <p className="muted small" style={{ marginTop: 8 }}>
              {trend.n_rated} rated of {trend.n_total} matching roasts.
            </p>
            {trend.points.length > 0 && (
              <table style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Roast</th>
                    <th>Stars</th>
                    <th>Inferred level</th>
                  </tr>
                </thead>
                <tbody>
                  {trend.points.map((p) => (
                    <tr key={p.roast_id}>
                      <td>{p.roast_date ?? "—"}</td>
                      <td>
                        <Link to={`/roasts/${encodeURIComponent(p.roast_id)}`}>
                          {p.roast_id}
                        </Link>
                      </td>
                      <td>{"★".repeat(p.stars)}{"☆".repeat(5 - p.stars)}</td>
                      <td className="muted small">{p.inferred_roast_level ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Overview -----------------------------------------------------------

function OverviewTab({
  profile,
  reference,
  referenceText,
  referenceErr,
  history,
  loading,
}: {
  profile: CoffeeProfile | null;
  reference: ReferenceRoastInfo | null;
  referenceText: string;
  referenceErr: unknown;
  history: RoastSummary[];
  loading: boolean;
}) {
  const quickStats = useMemo(() => computeQuickStats(history), [history]);

  return (
    <div className="col">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Hero roast</h2>
        <ErrorBox error={referenceErr} />
        {loading && !reference && !referenceErr ? (
          <p className="muted">Loading…</p>
        ) : reference ? (
          <div className="col">
            {referenceText && (
              <p className="muted small" style={{ margin: 0 }}>
                {referenceText}
              </p>
            )}
            <div
              className="row"
              style={{
                padding: "10px 12px",
                background: "var(--bg-alt)",
                borderRadius: "var(--radius)",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <Link
                to={`/roasts/${encodeURIComponent(reference.roast_id)}`}
                className="mono"
              >
                {reference.roast_id}
              </Link>
              <StarRating value={reference.stars} />
              <span className="muted">{formatDate(reference.roast_date)}</span>
              <span className="badge">{reference.source}</span>
            </div>
            {reference.rationale && (
              <p className="small muted" style={{ margin: 0 }}>
                {reference.rationale}
              </p>
            )}
          </div>
        ) : (
          <p className="muted">
            No hero roast yet.{" "}
            {history.length === 0
              ? "This coffee has no roasts on file."
              : "Rate a roast of this coffee ≥3★ to nominate a hero."}
          </p>
        )}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Quick stats</h2>
        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <div className="stat">
            <div className="label">Roasts on file</div>
            <div className="value">{quickStats.count}</div>
          </div>
          <div className="stat">
            <div className="label">Rated</div>
            <div className="value">{quickStats.rated}</div>
          </div>
          <div className="stat">
            <div className="label">Avg stars</div>
            <div className="value">
              {quickStats.avgStars != null
                ? quickStats.avgStars.toFixed(1)
                : "–"}
            </div>
          </div>
          <div className="stat">
            <div className="label">Last roasted</div>
            <div className="value" style={{ fontSize: "1.05em" }}>
              {quickStats.lastRoasted ?? "–"}
            </div>
          </div>
        </div>
      </div>

      {profile && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Profile</h2>
          <ProfileDetailTable profile={profile} />
        </div>
      )}
    </div>
  );
}

function ProfileDetailTable({ profile }: { profile: CoffeeProfile }) {
  const rows: [string, string][] = [
    ["Name", profile.name],
    ["Origin country", profile.origin_country ?? "–"],
    ["Region", profile.region ?? "–"],
    ["Farm", profile.farm ?? "–"],
    ["Process", profile.process ?? "–"],
    ["Variety", profile.variety ?? "–"],
    [
      "Density",
      profile.density_g_l != null ? `${profile.density_g_l} g/L` : "–",
    ],
    [
      "Moisture",
      profile.moisture_pct != null ? `${profile.moisture_pct}%` : "–",
    ],
    ["Screen size", profile.screen_size ?? "–"],
  ];
  return (
    <table>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td style={{ width: 180 }} className="muted">
              {k}
            </td>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function computeQuickStats(history: RoastSummary[]): {
  count: number;
  rated: number;
  avgStars: number | null;
  lastRoasted: string | null;
} {
  const count = history.length;
  const rated = history.filter((r) => r.stars != null && r.stars > 0).length;
  const starValues = history
    .map((r) => r.stars)
    .filter((s): s is number => s != null && s > 0);
  const avgStars =
    starValues.length > 0
      ? starValues.reduce((a, b) => a + b, 0) / starValues.length
      : null;
  // history comes back newest-first from get_coffee_history; take the first
  // with a date. `null` means this coffee has zero roasts on record.
  const lastWithDate = history.find((r) => r.roast_date != null);
  const lastRoasted = lastWithDate ? formatDate(lastWithDate.roast_date) : null;
  return { count, rated, avgStars, lastRoasted };
}

function buildProfileLine(p: CoffeeProfile): string {
  const parts = [
    p.origin_country,
    p.region,
    p.farm,
    p.process,
    p.variety,
  ].filter((x): x is string => !!x);
  return parts.join(" · ");
}

// --- Timeline -----------------------------------------------------------

function TimelineTab({
  history,
  summary,
  err,
  loading,
}: {
  history: RoastSummary[];
  summary: string;
  err: unknown;
  loading: boolean;
}) {
  return (
    <div>
      <ErrorBox error={err} />
      {summary && <p className="muted small">{summary}</p>}
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Roast</th>
            <th>Total</th>
            <th>Size</th>
            <th>Stars</th>
            <th>Tags</th>
          </tr>
        </thead>
        <tbody>
          {history.map((r) => (
            <tr key={r.roast_id}>
              <td>{formatDate(r.roast_date)}</td>
              <td>
                <Link to={`/roasts/${encodeURIComponent(r.roast_id)}`}>
                  {r.roast_id}
                </Link>
              </td>
              <td>{formatTime(r.total_roast_time_s)}</td>
              <td>{r.batch_size_g != null ? `${r.batch_size_g}g` : "–"}</td>
              <td>
                <StarRating value={r.stars ?? 0} />
              </td>
              <td>
                {rowTags(r).map((t) => (
                  <span className="badge" key={t}>
                    {t}
                  </span>
                ))}
              </td>
            </tr>
          ))}
          {!loading && history.length === 0 && (
            <tr>
              <td colSpan={6} className="muted" style={{ textAlign: "center" }}>
                No roasts on file for this coffee yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Consistency --------------------------------------------------------

function ConsistencyTab({
  report,
  text,
  err,
  loading,
}: {
  report: ConsistencyReport | null;
  text: string;
  err: unknown;
  loading: boolean;
}) {
  if (err) return <ErrorBox error={err} />;
  if (loading && !report) return <p className="muted">Loading…</p>;

  if (report == null) {
    return (
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          {text ||
            "Consistency scoring needs at least two roasts of this coffee on file. " +
              "Come back after your next batch."}
        </p>
      </div>
    );
  }

  return (
    <div className="col">
      <div className="card">
        <div className="row" style={{ alignItems: "flex-start", gap: 16 }}>
          <div
            className="stat"
            style={{ flex: "0 0 auto", minWidth: 140, textAlign: "center" }}
          >
            <div className="label">Score</div>
            <div className="value" style={{ fontSize: "2em" }}>
              {Math.round(report.score)}
            </div>
            <span className={`badge ${gradeClass(report.grade)}`}>
              {report.grade.replace(/_/g, " ")}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="muted small">
              Based on {report.n_roasts} roast
              {report.n_roasts === 1 ? "" : "s"}.
            </div>
            {report.rationale && (
              <p style={{ margin: "8px 0 0 0" }}>{report.rationale}</p>
            )}
            {text && !report.rationale && (
              <p className="muted small" style={{ margin: "8px 0 0 0" }}>
                {text}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Per-metric stats</h2>
        <PerMetricTable metrics={report.per_metric} />
      </div>

      {report.outliers.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Outliers</h2>
          <table>
            <thead>
              <tr>
                <th>Roast</th>
                <th>Metric</th>
                <th>Value</th>
                <th>Mean</th>
                <th>Δ σ</th>
              </tr>
            </thead>
            <tbody>
              {report.outliers.map((o, i) => (
                <tr key={`${o.roast_id}-${o.metric}-${i}`}>
                  <td>
                    <Link to={`/roasts/${encodeURIComponent(o.roast_id)}`}>
                      {o.roast_id}
                    </Link>
                  </td>
                  <td>{prettyMetric(o.metric)}</td>
                  <td className="mono">{formatMetricValue(o.metric, o.value)}</td>
                  <td className="mono">{formatMetricValue(o.metric, o.mean)}</td>
                  <td className="mono">
                    {o.delta_stddevs >= 0 ? "+" : ""}
                    {o.delta_stddevs.toFixed(1)}σ
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PerMetricTable({ metrics }: { metrics: MetricStats[] }) {
  if (metrics.length === 0) {
    return (
      <p className="muted small">
        Not enough data yet — at least two roasts needed per metric.
      </p>
    );
  }
  return (
    <table>
      <thead>
        <tr>
          <th>Metric</th>
          <th>Mean</th>
          <th>Stddev</th>
          <th>CV</th>
          <th>Range</th>
          <th>N</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map((m) => (
          <tr key={m.name}>
            <td>{prettyMetric(m.name)}</td>
            <td className="mono">{formatMetricValue(m.name, m.mean)}</td>
            <td className="mono">{formatMetricValue(m.name, m.stddev)}</td>
            <td className="mono">{(m.cv * 100).toFixed(1)}%</td>
            <td className="mono">
              {formatMetricValue(m.name, m.min)} –{" "}
              {formatMetricValue(m.name, m.max)}
            </td>
            <td className="mono">{m.n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- Metric formatting --------------------------------------------------

// Metric names come through from consistency scoring as snake_case keys —
// "time_yellow_to_fc", "max_ror", "drop_temp", etc. Map the common ones to
// pretty labels; fall back to a best-effort transform for unknown names.
function prettyMetric(m: string): string {
  const table: Record<string, string> = {
    max_ror: "Max RoR",
    ror_at_first_crack: "RoR at first crack",
    end_ror: "End RoR",
    time_yellow_to_fc: "Yellow → FC",
    time_fc_to_drop: "FC → drop",
    development_time_ratio: "Development ratio",
    drop_time_s: "Drop time",
    drop_temp: "Drop temperature",
    charge_temp: "Charge temperature",
    turning_point_temp: "TP temperature",
  };
  if (table[m]) return table[m];
  return m.replace(/_/g, " ");
}

// Format by metric family. Times → mm:ss; ratios → pct; temps → 1 dp;
// RoR → "/min"; fallback → plain number.
function formatMetricValue(metric: string, value: number): string {
  if (metric.includes("time") && !metric.includes("ratio")) {
    return formatTime(value);
  }
  if (metric.includes("ratio") || metric.includes("percent")) {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (metric.includes("temp")) {
    return `${value.toFixed(1)}°`;
  }
  if (metric.includes("ror")) {
    return `${value.toFixed(1)}/min`;
  }
  return formatNumber(value, 2);
}

// Color-code the grade pill. Keep it subtle — no bright alarm reds for
// "inconsistent" because one inconsistent coffee is not an emergency.
function gradeClass(grade: string): "ok" | "warn" | "err" | "" {
  switch (grade) {
    case "excellent":
    case "good":
      return "ok";
    case "variable":
      return "warn";
    case "inconsistent":
      return "err";
    default:
      return "";
  }
}
